import { useState, useEffect, useMemo, useRef } from "react";
import { formatAmount } from "@/lib/utils/formatters";
import { formatPhoneNumber } from "../utils/applicationFormatters";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { getPropertyAgentLabels } from "@lib/utils/propertyAgentLabels";
import { getPropertyEditUrl } from "@/lib/utils/propertyNavigation";
import { showError, showInfo, showSuccess } from "@/lib/utils/notifications";
import { sendAnalytics } from "@/lib/utils/analytics";
import {
  getProperties,
  lookupInvite,
  validateInvite,
  type InviteLookupRow,
} from "@/features/application/services/applicationService";
import type { InviteValidateResponse } from "../types/api";
import { getPropertyById, type Property as PropertyDetailRecord } from "@/lib/api/propertyService";
import { resendInvitation } from "@/features/invitee/services/inviteeService";
import { getStates, type State } from "@/lib/api/statesService";
import { confirmResendInvite } from "../utils/resendInviteHandlers";
import {
  validatePropertyTab,
  type PropertyTabFormData,
  type PropertyTabInlineField,
  type CoResidentValidatedField,
} from "../utils/propertyTabValidator";
import type {
  PropertyTabProps,
  CoResidentFormRow,
  PropertyTabPropertyRecord,
  PropertyTabLocalFormData,
  PropertyTabFormChangePayload,
} from "../types/tabs";
import {
  buildPropertyTabFormChangePayload,
  createEmptyCoResidentRow,
  isPropertyInlineField,
} from "../utils/propertyTabFormChange";
import {
  getSelectedPropertyLeadDays,
  isLeaseStartBeyondLeadTime,
} from "../utils/propertyTabLeadTime";
import type { PropertyLeadTimeContext } from "../types/utils";
import { usePropertyTabAdvanceSearch } from "./usePropertyTabAdvanceSearch";
import { usePropertyTabAgents } from "./usePropertyTabAgents";
import {
  EDIT_LEASE_DURATION_MONTHS,
  canRemoveCoResident,
  computeHasApplied,
  formatAgentPhoneValue,
  getSavedPropertyId,
  getViewLeasingConsultantFields,
  mapInviteCoApplicantsToRows,
  hasLeaseStartDateChanged,
  hasRentAmountChanged,
  normalizeRentForCompare,
  parsePropertyLeaseDate,
  resolvePropertyCoResidentFormState,
  resolveInitialAgentId,
  resolveInitialPropertyState,
  sanitizeLeaseDuration,
  shouldConfirmRentAmount,
  shouldShowPropertyNotWorkingAlert,
  trim,
} from "../components/tabs/propertyTabHelpers";

export function usePropertyTab({
  applicationAllData,
  applicationData,
  applicantId: _applicantId,
  isEdit = false,
  onFormChange,
  exposePropertyInlineValidation = false,
  onOpenRentChangePopup,
  hasContractData = false,
}: PropertyTabProps) {
  const property =
    applicationAllData?.property || applicationAllData?.temp_property;
  const agent =
    applicationAllData?.agent ||
    applicationAllData?.other_agent ||
    applicationAllData?.temp_agent;
  const propertyName = property?.property_name ?? applicationAllData?.property_name;

  const isPrimary =
    applicationData?.is_primary === 1 ||
    applicationData?.is_primary === "1" ||
    applicationData?.is_primary === true;
  
  const displayInviteCode = applicationData?.applicant_invite_code || (isPrimary ? applicationAllData?.invite_code : "");
  const agentLabels = getPropertyAgentLabels(propertyName);
  const leasingConsultantLabel = "Leasing Consultant";

  const getInitialLeaseDate = () => {
    return parsePropertyLeaseDate(applicationAllData?.lease_start_date);
  };

  /** Saved values at load — refs so calendar onChange can compare without waiting for blur. */
  const originalRentAmountRef = useRef(applicationAllData?.rent_amount || "");
  const originalLeaseStartDateRef = useRef(getInitialLeaseDate());

  const initialCoResidentState = resolvePropertyCoResidentFormState(
    applicationAllData,
    applicationData,
  );

  const [formData, setFormData] = useState<PropertyTabLocalFormData>({
    property_id: String(property?.property_id ?? applicationAllData?.fk_property_id ?? ""),
    property_name: property?.property_name ?? applicationAllData?.property_name ?? "",
    property_state: resolveInitialPropertyState(applicationAllData, property),
    rent_amount: applicationAllData?.rent_amount || "",
    unit_number: applicationAllData?.unit_number || "",
    lease_start_date: getInitialLeaseDate(),
    lease_duration: sanitizeLeaseDuration(applicationAllData?.lease_duration),
    other_individuals: initialCoResidentState.other_individuals,
    rent_lease_reason: applicationAllData?.rent_lease_reason || "",
    agent_id: resolveInitialAgentId(applicationAllData),
    agent_name:
      agent?.first_name && agent?.last_name
        ? `${agent.first_name} ${agent.last_name}`
        : agent?.agent_name || "",
    agent_phone: formatAgentPhoneValue(agent?.mobile_no ?? agent?.agent_mobile_no),
    agent_email: agent?.email || agent?.agent_email || "",
    has_applied: computeHasApplied(applicationAllData),
    invite_code: displayInviteCode || "",
    co_residents: initialCoResidentState.co_residents as CoResidentFormRow[],
  });

  const [propertyOptions, setPropertyOptions] = useState<SearchableSelectOption[]>([]);
  const [propertyOptionsLoading, setPropertyOptionsLoading] = useState(false);
  const [selectedPropertyData, setSelectedPropertyData] = useState<PropertyTabPropertyRecord | null>(
    property && property?.property_id ? property : null,
  );
  const [leaseLeadConfirm, setLeaseLeadConfirm] = useState<{
    previousValue: string;
  } | null>(null);
  const [rentAmountConfirm, setRentAmountConfirm] = useState<string | null>(
    null,
  );
  const [resendInviteConfirm, setResendInviteConfirm] = useState<{
    inviteCode: string;
  } | null>(null);
  const [resendInviteLoading, setResendInviteLoading] = useState(false);
  const resendInviteInFlightRef = useRef(false);

  /** US states for editable Property tab state field (admin edit). */
  const [propertyTabStates, setPropertyTabStates] = useState<State[]>([]);

  const [propertyFieldDirty, setPropertyFieldDirty] = useState<
    Partial<Record<PropertyTabInlineField, boolean>>
  >({});

  const lastSyncedPropertyIdRef = useRef<string>("");
  const leadTimeDetailFetchRef = useRef<Set<string>>(new Set());
  const latestLeaseStartDateRef = useRef(formData.lease_start_date);
  const rentAmountAtFocusRef = useRef<string>("");

  /** Sticky once rent or lease start date changes from saved values and activates Reason. */
  const [rentLeaseReasonActive, setRentLeaseReasonActive] = useState(false);

  // Angular `lookupInvite()`.
  const [inviteLookupOpen, setInviteLookupOpen] = useState(false);
  const [inviteLookupResults, setInviteLookupResults] = useState<InviteLookupRow[]>([]);
  const [lookingUpInvite, setLookingUpInvite] = useState(false);

  // Angular `validateInvite()`.
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [inviteExpiredConfirm, setInviteExpiredConfirm] = useState<{
    newCode: string;
  } | null>(null);

  const handleInviteLookup = async () => {
    const email = applicationData?.user_data?.email as string | undefined;
    const mobile = applicationData?.user_data?.mobile_no as
      | string
      | undefined;
    if (!email && !mobile) {
      showError("Applicant has no email or mobile number to look up.");
      return;
    }
    setLookingUpInvite(true);
    try {
      const params: { email?: string; mobile_no?: string } = {};
      if (email) params.email = email;
      if (mobile) params.mobile_no = mobile;
      const rows = await lookupInvite(params);
      const matches = rows.filter((row) => Boolean(row.invite_code));
      // Angular: open dialog only when matches exist.
      if (matches.length === 0) {
        showInfo("No invitation found for this applicant.");
        return;
      }
      setInviteLookupResults(matches);
      setInviteLookupOpen(true);
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message;
      showError(typeof msg === "string" && msg ? msg : "Failed to look up invites.");
    } finally {
      setLookingUpInvite(false);
    }
  };

  const handleInviteSelected = (code: string) => {
    setInviteLookupOpen(false);
    handleInputChange("invite_code", code);
    // Angular: validate picked code immediately.
    void runValidateInvite(code);
  };

  useEffect(() => {
    latestLeaseStartDateRef.current = formData.lease_start_date;
  }, [formData.lease_start_date]);

  const hasRentOrLeaseChanged = (): boolean =>
    hasRentAmountChanged(formData.rent_amount, originalRentAmountRef.current) ||
    hasLeaseStartDateChanged(
      formData.lease_start_date,
      originalLeaseStartDateRef.current,
    );

  const syncPropertyFormChange = (
    newFormData: PropertyTabLocalFormData,
    forceReasonActive = false,
  ) => {
    const reasonActive = forceReasonActive || rentLeaseReasonActive;
    onFormChange?.({
      ...buildPropertyTabFormChangePayload(newFormData, {
        property,
        applicationAllData,
      }),
      ...(reasonActive ? { rent_lease_reason_active: true } : {}),
    });
  };

  const activateRentLeaseReason = (data: PropertyTabLocalFormData = formData) => {
    if (rentLeaseReasonActive) return;
    setRentLeaseReasonActive(true);
    syncPropertyFormChange(data, true);
  };

  /** Angular: co-resident phone/relationship validators omitted when primary has invite code. */
  const inviteRelaxedCoResident = Boolean(applicationAllData?.invite_code);

  const openPropertyInReact = (propertyId: string | number) => {
    window.open(getPropertyEditUrl(propertyId), "_blank", "noopener,noreferrer");
  };

  const propertyTabValidation = useMemo(() => {
    const data: PropertyTabFormData = {
      has_applied: formData.has_applied,
      property_name: formData.property_name,
      property_state: formData.property_state,
      rent_amount: formData.rent_amount,
      lease_start_date: formData.lease_start_date,
      lease_duration: formData.lease_duration,
      unit_number: formData.unit_number,
      rent_lease_reason: formData.rent_lease_reason,
      invite_code: formData.invite_code,
      other_individuals: formData.other_individuals,
      co_residents: formData.co_residents,
      agent_id: formData.agent_id,
      agent_name: formData.agent_name,
      agent_email: formData.agent_email,
      agent_phone: formData.agent_phone,
    };
    return validatePropertyTab(data, {
      primaryEmail:
        applicationData?.user_data?.email ?? applicationData?.email,
      primaryMobile:
        applicationData?.user_data?.mobile_no ?? applicationData?.mobile_no,
      requireRentLeaseReason: rentLeaseReasonActive,
      coResidentInviteRelaxed: inviteRelaxedCoResident,
    });
  }, [
    formData,
    applicationData,
    inviteRelaxedCoResident,
    rentLeaseReasonActive,
  ]);

  const leaseDurationOptions = useMemo(() => {
    const options = [
      {
        value: String(EDIT_LEASE_DURATION_MONTHS),
        label: `${EDIT_LEASE_DURATION_MONTHS} Months`,
      },
    ];
    const currentVal = formData.lease_duration;
    if (currentVal && String(currentVal) !== String(EDIT_LEASE_DURATION_MONTHS)) {
      options.push({
        value: String(currentVal),
        label: `${currentVal} Months`,
      });
    }
    return options;
  }, [formData.lease_duration]);

  const propertyInlineMessage = (
    field: PropertyTabInlineField,
  ): string | undefined => {
    const msg = propertyTabValidation.fieldErrors[field];
    if (!msg) return undefined;
    if (exposePropertyInlineValidation || propertyFieldDirty[field]) return msg;
    return undefined;
  };

  const coResidentFieldMsg = (
    index: number,
    field: CoResidentValidatedField,
  ): string | undefined => {
    const msg = propertyTabValidation.coResidentFieldErrors?.[index]?.[field];
    if (!msg) return undefined;
    if (exposePropertyInlineValidation || propertyFieldDirty.co_residents)
      return msg;
    return undefined;
  };

  const handleCoResidentPhoneInput = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 15);
    handleCoResidentChange(index, "phone", formatPhoneNumber(digits));
  };

  const handleAgentPhoneInput = (raw: string) => {
    handleInputChange("agent_phone", formatAgentPhoneValue(raw));
  };

  // Re-init form when data or edit mode changes.
  useEffect(() => {
    if (applicationAllData) {
      const newAgent =
        applicationAllData?.agent ||
        applicationAllData?.other_agent ||
        applicationAllData?.temp_agent;

      const parsedDate = parsePropertyLeaseDate(applicationAllData?.lease_start_date);
      originalLeaseStartDateRef.current = parsedDate;
      originalRentAmountRef.current = applicationAllData?.rent_amount || "";

      const coResidentState = resolvePropertyCoResidentFormState(
        applicationAllData,
        applicationData,
      );

      setFormData({
        property_id: String(applicationAllData?.property?.property_id ?? applicationAllData?.fk_property_id ?? ""),
        property_name:
          applicationAllData?.property?.property_name ??
          applicationAllData?.property_name ??
          "",
        property_state: resolveInitialPropertyState(applicationAllData, property),
        rent_amount: applicationAllData?.rent_amount || "",
        unit_number: applicationAllData?.unit_number || "",
        lease_start_date: parsedDate,
        lease_duration: sanitizeLeaseDuration(applicationAllData?.lease_duration),
        other_individuals: coResidentState.other_individuals,
        rent_lease_reason: applicationAllData?.rent_lease_reason || "",
        agent_id: resolveInitialAgentId(applicationAllData),
        agent_name:
          newAgent?.first_name && newAgent?.last_name
            ? `${newAgent.first_name} ${newAgent.last_name}`
            : newAgent?.agent_name || "",
        agent_phone: formatAgentPhoneValue(
          newAgent?.mobile_no ?? newAgent?.agent_mobile_no,
        ),
        agent_email: newAgent?.email || newAgent?.agent_email || "",
        has_applied: computeHasApplied(applicationAllData),
        invite_code: displayInviteCode || "",
        co_residents: coResidentState.co_residents,
      });
      setRentLeaseReasonActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationAllData, applicationData, isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    if (selectedPropertyData && selectedPropertyData?.property_id) return;
    if (property && property?.property_id) {
      setSelectedPropertyData(property);
    }
  }, [isEdit, property, selectedPropertyData]);

  useEffect(() => {
    // Seed property dropdown label before options load (SearchableSelect shows raw id otherwise).
    if (!isEdit) return;

    const idRaw =
      applicationAllData?.property?.property_id ??
      applicationAllData?.fk_property_id ??
      property?.property_id;
    const nameRaw =
      applicationAllData?.property?.property_name ??
      applicationAllData?.property_name ??
      property?.property_name;

    const id = idRaw != null ? String(idRaw) : "";
    const label = nameRaw != null ? String(nameRaw) : "";
    if (!id || !label) return;

    setPropertyOptions((prev) => {
      if (prev.some((o) => String(o.value) === id)) return prev;
      return [{ value: id, label, data: applicationAllData?.property ?? property }, ...prev];
    });
  }, [isEdit, applicationAllData, property]);

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    void getStates({ zip_flag: "1" }).then((rows) => {
      if (!cancelled) setPropertyTabStates(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  const loadProperties = async (searchText: string) => {
    setPropertyOptionsLoading(true);
    try {
      const rows = await getProperties(searchText);
      const opts: SearchableSelectOption[] = rows.map((p) => ({
        value: String(p.property_id),
        label: String(p.property_name ?? ""),
        data: p as PropertyTabPropertyRecord,
      }));
      setPropertyOptions(opts);
    } catch (e) {
      showError((e as { message?: string })?.message || "Failed to fetch properties.");
      setPropertyOptions([]);
    } finally {
      setPropertyOptionsLoading(false);
    }
  };

  const leadTimeCtx = (
    overrides?: Partial<PropertyLeadTimeContext>,
  ): PropertyLeadTimeContext => ({
    propertyIdValue: String(formData.property_id || ""),
    selectedPropertyData,
    propertyOptions,
    fallbackProperty: property,
    ...overrides,
  });

  const applySelectedProperty = (
    propertyId: string,
    opts?: { data?: PropertyTabPropertyRecord },
  ) => {
    const selected = propertyOptions.find(
      (o) => String(o.value) === String(propertyId),
    ) as SearchableSelectOption | undefined;
    const data = (opts?.data ?? selected?.data) as PropertyTabPropertyRecord | undefined;

    setSelectedPropertyData(data ?? null);
    const nextState =
      data?.property_state != null && trim(data.property_state) !== ""
        ? String(data.property_state)
        : undefined;
    const nextName = (selected?.label ?? data?.property_name ?? formData.property_name) as string;

    const savedPropertyId = getSavedPropertyId(applicationAllData);
    const isSameAsSaved =
      savedPropertyId !== "" && savedPropertyId === String(propertyId);
    const clearAgent = !isSameAsSaved;

    setFormData((prev) => ({
      ...prev,
      property_id: String(propertyId),
      property_name: nextName,
      ...(nextState !== undefined ? { property_state: nextState } : {}),
      ...(clearAgent
        ? {
            agent_id: "",
            agent_name: "",
            agent_email: "",
            agent_phone: "",
          }
        : {}),
    }));
    setPropertyFieldDirty((prev) => ({ ...prev, property_name: true }));

    if (String(formData.property_id) !== String(propertyId)) {
      lastSyncedPropertyIdRef.current = String(formData.property_id);
    }

    onFormChange?.({
      has_applied: formData.has_applied,
      applied_property: formData.has_applied === false ? "0" : "1",
      fk_property_id: String(propertyId),
      property_name: nextName,
      ...(nextState !== undefined ? { property_state: nextState } : {}),
      ...(rentLeaseReasonActive ? { rent_lease_reason_active: true } : {}),
    });

    if (
      formData.lease_start_date &&
      isLeaseStartBeyondLeadTime(
        formData.lease_start_date,
        leadTimeCtx({ propertyIdValue: String(propertyId), propertyData: data }),
      )
    ) {
      setLeaseLeadConfirm({ previousValue: originalLeaseStartDateRef.current || "" });
    }
  };

  /** Angular `validateInvite()` success branch: prefill property/rent/lease + co-residents. */
  const applyValidatedInvite = async (
    inviteCode: string,
    info: InviteValidateResponse,
  ) => {
    const propertyId = info.property_id != null ? String(info.property_id) : "";
    const rentAmount = info.rent_amount != null ? String(info.rent_amount) : "";
    const leaseStartDate = parsePropertyLeaseDate(info.lease_start_date);
    const leaseDuration = info.lease_duration
      ? String(info.lease_duration)
      : String(EDIT_LEASE_DURATION_MONTHS);
    const unitNumber = info.unit_number != null ? String(info.unit_number) : "";

    // Angular: co-residents only for primary applicant.
    const coRows = isPrimary ? mapInviteCoApplicantsToRows(info.co_applicant) : [];
    const otherIndividuals = isPrimary && coRows.length > 0;

    setFormData((prev) => ({
      ...prev,
      invite_code: inviteCode,
      has_applied: true,
      ...(rentAmount ? { rent_amount: rentAmount } : {}),
      ...(leaseStartDate ? { lease_start_date: leaseStartDate } : {}),
      lease_duration: leaseDuration,
      ...(unitNumber ? { unit_number: unitNumber } : {}),
      ...(isPrimary
        ? { other_individuals: otherIndividuals, co_residents: coRows }
        : {}),
    }));

    // Angular `propertySelect`: load address/state/agents for invited property.
    if (propertyId) {
      try {
        const detail = await getPropertyById(propertyId);
        setSelectedPropertyData(detail);
        setPropertyOptions((prev) =>
          prev.some((o) => String(o.value) === propertyId)
            ? prev.map((o) =>
                String(o.value) === propertyId
                  ? {
                      ...o,
                      label: String(detail?.property_name ?? o.label),
                      data: {
                        ...((o.data as Record<string, unknown>) ?? {}),
                        ...detail,
                      },
                    }
                  : o,
              )
            : [
                {
                  value: propertyId,
                  label: String(detail?.property_name ?? ""),
                  data: detail,
                },
                ...prev,
              ],
        );
        applySelectedProperty(propertyId, {
          data: detail as PropertyTabPropertyRecord,
        });
      } catch {
        applySelectedProperty(propertyId);
      }
    }

    // Emit merged delta last so applied_property "1" wins over stale values.
    const delta: PropertyTabFormChangePayload = {
      has_applied: true,
      applied_property: "1",
      invite_code: inviteCode,
      lease_duration: leaseDuration,
    };
    if (rentAmount) delta.rent_amount = rentAmount;
    if (leaseStartDate) delta.lease_start_date = leaseStartDate;
    if (unitNumber) delta.unit_number = unitNumber;
    if (isPrimary) {
      delta.other_individuals = otherIndividuals ? "1" : "0";
      delta.co_residents = coRows;
    }
    onFormChange?.(delta);

    showSuccess("Invite code validated.");
  };

  const runValidateInvite = async (code: string) => {
    const inviteCode = trim(code);
    if (!inviteCode || validatingInvite) return;
    setValidatingInvite(true);
    try {
      const info = await validateInvite(inviteCode);
      if (!info) return;
      if (info.new_invite_code) {
        setInviteExpiredConfirm({ newCode: String(info.new_invite_code) });
        return;
      }
      await applyValidatedInvite(inviteCode, info);
    } catch (error: unknown) {
      const msg = (error as { message?: unknown })?.message;
      showError(
        typeof msg === "string" && msg ? msg : "Failed to validate invite code.",
      );
    } finally {
      setValidatingInvite(false);
    }
  };

  const handleValidateInvite = () => {
    sendAnalytics('click', 'validate invite', _applicantId)
    void runValidateInvite(formData.invite_code);
  };

  const confirmInviteExpired = () => {
    const newCode = inviteExpiredConfirm?.newCode;
    setInviteExpiredConfirm(null);
    if (!newCode) return;
    handleInputChange("invite_code", newCode);
    void runValidateInvite(newCode);
  };

  useEffect(() => {
    if (!isEdit || formData.has_applied === false) return;
    const propertyId = String(formData.property_id || "");
    if (!propertyId || getSelectedPropertyLeadDays(leadTimeCtx({ propertyIdValue: propertyId }))) return;
    if (leadTimeDetailFetchRef.current.has(propertyId)) return;

    let cancelled = false;
    leadTimeDetailFetchRef.current.add(propertyId);
    void getPropertyById(propertyId)
      .then((detail: PropertyDetailRecord) => {
        if (cancelled) return;
        setSelectedPropertyData((prev) =>
          String(prev?.property_id ?? "") === propertyId
            ? { ...prev, ...detail }
            : detail,
        );
        setPropertyOptions((prev) =>
          prev.map((option) =>
            String(option.value) === propertyId
              ? {
                  ...option,
                  label: String(detail?.property_name ?? option.label),
                  data: { ...((option.data as Record<string, unknown>) ?? {}), ...detail },
                }
              : option,
          ),
        );
        const latestLeaseStartDate = latestLeaseStartDateRef.current;
        if (
          latestLeaseStartDate &&
          isLeaseStartBeyondLeadTime(
            latestLeaseStartDate,
            leadTimeCtx({ propertyIdValue: propertyId, propertyData: detail }),
          )
        ) {
          setLeaseLeadConfirm({ previousValue: originalLeaseStartDateRef.current || "" });
        }
      })
      .catch(() => {
        // Non-blocking if property detail fetch fails.
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isEdit,
    formData.has_applied,
    formData.property_id,
    formData.lease_start_date,
  ]);

  const handleInputChange = (field: string, value: unknown) => {
    const newFormData = { ...formData, [field]: value };
    
    // Angular checkCoResidentGroup(): Yes adds row when empty; No clears co_residents.
    if (field === "other_individuals" && value === true && newFormData.co_residents.length === 0) {
      newFormData.co_residents = [createEmptyCoResidentRow()];
    }
    if (field === "other_individuals" && value === false) {
      newFormData.co_residents = [];
    }
    if (field === "other_individuals" && value === true) {
      setPropertyFieldDirty((prev) => ({ ...prev, co_residents: true }));
    }
    
    setFormData(newFormData);

    if (isPropertyInlineField(field)) {
      setPropertyFieldDirty((prev) => ({
        ...prev,
        [field]: true,
      }));
    }

    syncPropertyFormChange(newFormData);

    if (field === "lease_start_date") {
      if (
        hasLeaseStartDateChanged(value, originalLeaseStartDateRef.current)
      ) {
        activateRentLeaseReason(newFormData);
      }
      if (isLeaseStartBeyondLeadTime(value, leadTimeCtx())) {
        setLeaseLeadConfirm({
          previousValue: originalLeaseStartDateRef.current || "",
        });
      }
    }
  };

  const resetLeaseStartAfterLeadWarning = () => {
    const previousValue = leaseLeadConfirm?.previousValue || "";
    setLeaseLeadConfirm(null);
    setFormData((prev) => ({
      ...prev,
      lease_start_date: previousValue,
    }));
    onFormChange?.({
      has_applied: formData.has_applied,
      applied_property: formData.has_applied === false ? "0" : "1",
      property_name: formData.property_name,
      fk_property_id: formData.property_id || undefined,
      rent_amount: formData.rent_amount ?? "",
      lease_start_date: previousValue,
      lease_duration: formData.lease_duration,
      unit_number: formData.unit_number,
      property_state: formData.property_state,
      rent_lease_reason: formData.rent_lease_reason,
      other_individuals: formData.other_individuals ? "1" : "0",
      co_residents: formData.co_residents,
      agent_id: String(formData.agent_id ?? ""),
      agent_name: formData.agent_name ?? "",
      agent_email: formData.agent_email ?? "",
      agent_phone: formData.agent_phone ?? "",
      ...(rentLeaseReasonActive ? { rent_lease_reason_active: true } : {}),
    });
  };

  const handleRentAmountFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    rentAmountAtFocusRef.current = normalizeRentForCompare(
      event.currentTarget.value || formData.rent_amount,
    );
  };

  const handleRentAmountBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const blurValue = normalizeRentForCompare(
      event.currentTarget.value || formData.rent_amount,
    );
    if (blurValue !== rentAmountAtFocusRef.current) {
      activateRentLeaseReason();
    }

    const rentAmount = event.currentTarget.value || formData.rent_amount;
    if (shouldConfirmRentAmount(rentAmount)) {
      const numericRent = String(rentAmount).replace(/[^0-9.]+/g, "");
      setRentAmountConfirm(formatAmount(numericRent || formData.rent_amount));
    }
  };

  const handleLeaseStartBlur = () => {
    if (
      hasLeaseStartDateChanged(
        formData.lease_start_date,
        originalLeaseStartDateRef.current,
      )
    ) {
      activateRentLeaseReason(formData);
    }
  };

  const handleCoResidentChange = (
    index: number,
    field: string,
    value: string | boolean,
  ) => {
    const updatedCoResidents = [...formData.co_residents];
    updatedCoResidents[index] = { ...updatedCoResidents[index], [field]: value };
    handleInputChange("co_residents", updatedCoResidents);
  };

  const addCoResident = () => {
    setPropertyFieldDirty((prev) => ({ ...prev, co_residents: true }));
    const updatedCoResidents = [...formData.co_residents, createEmptyCoResidentRow()];
    handleInputChange("co_residents", updatedCoResidents);
  };

  const removeCoResident = (index: number) => {
    const resident = formData.co_residents[index];
    if (!resident || !canRemoveCoResident(resident)) return;

    const updatedCoResidents = formData.co_residents.filter((_, i) => i !== index);
    handleInputChange("co_residents", updatedCoResidents);
  };

  const handleResendInvite = async () => {
    const inviteCode = resendInviteConfirm?.inviteCode;
    if (!inviteCode || resendInviteInFlightRef.current) return;

    // Guard double Yes clicks (two GETs) before loading state commits.
    resendInviteInFlightRef.current = true;
    setResendInviteLoading(true);
    try {
      await confirmResendInvite({
        inviteCode,
        resendInvitation,
        closeConfirm: () => setResendInviteConfirm(null),
      });
    } finally {
      resendInviteInFlightRef.current = false;
      setResendInviteLoading(false);
    }
  };

  const propertyOnFormChange = (payload: PropertyTabFormChangePayload) => {
    onFormChange?.({
      ...payload,
      ...(rentLeaseReasonActive ? { rent_lease_reason_active: true } : {}),
    });
  };

  const agents = usePropertyTabAgents({
    isEdit,
    formData,
    setFormData,
    applicationAllData,
    onFormChange: propertyOnFormChange,
    lastSyncedPropertyIdRef,
  });

  const advanceSearch = usePropertyTabAdvanceSearch({
    applySelectedProperty,
    setPropertyOptions,
    setSelectedPropertyData,
  });

  // Angular edit: leasing agent section only when applied_property === '1'.
  const showLeasingAgentSection = formData.has_applied === true;

  const viewLeasing = getViewLeasingConsultantFields(applicationAllData, property);
  const hasViewLeasingInfo = Boolean(
    viewLeasing.name || viewLeasing.email || viewLeasing.phone,
  );

  const showPropertyNotWorkingAlert = shouldShowPropertyNotWorkingAlert({
    propertyIdValue: formData.property_id,
    selectedPropertyData,
    property,
    applicationAllData,
  });

  return {
    applicationAllData,
    applicationData,
    isEdit,
    onOpenRentChangePopup,
    hasContractData,
    property,
    displayInviteCode,
    agentLabels,
    formData,
    propertyOptions,
    propertyOptionsLoading,
    selectedPropertyData,
    ...advanceSearch,
    leaseLeadConfirm,
    rentAmountConfirm,
    resendInviteConfirm,
    resendInviteLoading,
    propertyTabStates,
    agentOptions: agents.agentOptions,
    loadingAgents: agents.loadingAgents,
    inviteLookupOpen,
    inviteLookupResults,
    lookingUpInvite,
    validatingInvite,
    inviteExpiredConfirm,
    leaseDurationOptions,
    propertyInlineMessage,
    inviteRelaxedCoResident,
    coResidentFieldMsg,
    showLeasingAgentSection,
    viewLeasing,
    hasViewLeasingInfo,
    leasingConsultantLabel,
    showPropertyNotWorkingAlert,
    handleInputChange,
    handleInviteLookup,
    handleValidateInvite,
    confirmInviteExpired,
    setInviteExpiredConfirm,
    handleOpenAdvanceSearch: advanceSearch.handleOpenAdvanceSearch,
    setIsAdvanceSearchOpen: advanceSearch.setIsAdvanceSearchOpen,
    handleApplyAdvanceSearch: advanceSearch.handleApplyAdvanceSearch,
    handleAdvanceSearchStateChange: advanceSearch.handleAdvanceSearchStateChange,
    handleAdvanceSearchCityChange: advanceSearch.handleAdvanceSearchCityChange,
    setAdvanceSearchSelectedId: advanceSearch.setAdvanceSearchSelectedId,
    applySelectedProperty,
    loadProperties,
    handleRentAmountFocus,
    handleRentAmountBlur,
    handleLeaseStartBlur,
    applyAgentSelection: agents.applyAgentSelection,
    handleAgentPhoneInput,
    handleCoResidentChange,
    handleCoResidentPhoneInput,
    addCoResident,
    removeCoResident,
    setResendInviteConfirm,
    setRentAmountConfirm,
    setLeaseLeadConfirm,
    resetLeaseStartAfterLeadWarning,
    handleResendInvite,
    openPropertyInReact,
    setInviteLookupOpen,
    handleInviteSelected,
    showRentLeaseReason: rentLeaseReasonActive,
    hasRentOrLeaseChanged,
  };
}

export type PropertyTabState = ReturnType<typeof usePropertyTab>;
